/** SUNTRONIX ONE — Commercial Go-Live Report.
 *
 *  Route: /administration/go-live-report
 *  Read-only 13-check matrix rendered from /api/go-live/report + a
 *  product-quality snapshot + a Vtiger reconciliation snapshot.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Check, AlertTriangle, RefreshCw, Rocket, PackageSearch,
  Database, ChevronRight,
} from "lucide-react";

export default function GoLiveReport() {
  const [report, setReport] = useState(null);
  const [pq, setPq] = useState(null);
  const [recon, setRecon] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const [r, p, rc] = await Promise.all([
        api.get("/go-live/report"),
        api.get("/go-live/product-quality"),
        api.get("/go-live/vtiger-reconciliation"),
      ]);
      setReport(r.data); setPq(p.data); setRecon(rc.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const overall  = report?.overall || "…";
  const overallOk = overall === "READY_FOR_INTERNAL_PILOT";

  return (
    <div className="max-w-[1200px] mx-auto space-y-4" data-testid="go-live-report">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
            <Rocket className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold text-slate-900">
              Commercial Go-Live Report
            </div>
            <div className="text-[11px] text-slate-500">
              Live snapshot of Suntronix ONE readiness for the internal pilot
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={"text-[12px] px-3 py-1 rounded-full ring-1 font-semibold " +
                                      (overallOk
                                          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                          : "bg-amber-50 text-amber-700 ring-amber-200")}>
            {(overall || "").replace(/_/g, " ")}
          </span>
          <button onClick={load} disabled={busy}
              data-testid="go-live-refresh"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} />
            Refresh
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 border border-slate-200 rounded-lg bg-white">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-900">
            13 · Readiness checks
          </div>
          <div className="divide-y divide-slate-100">
            {(report?.checks || []).map((c) => (
              <div key={c.label} className="px-3 py-2 flex items-center gap-3 text-[12px]"
                  data-testid={`gl-check-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {c.status === "PASS"
                    ? <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                    : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{c.label}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.detail}</div>
                </div>
                <span className={"text-[10px] px-1.5 py-0.5 rounded ring-1 whitespace-nowrap " +
                    (c.status === "PASS"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-amber-50 text-amber-700 ring-amber-200")}>
                  {c.status.replace(/_/g, " ")}
                </span>
                {c.action_link && (
                  <Link to={c.action_link}
                      className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-[11px]">
                    Open <ChevronRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="border border-slate-200 rounded-lg bg-white p-3">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-900">
              <PackageSearch className="h-3.5 w-3.5 text-slate-500" /> Product quality
            </div>
            {pq ? (
              <div className="mt-2 space-y-1 text-[11px]">
                <Row label="Total products" value={pq.total} />
                <Row label="Complete records" value={pq.complete} tone="emerald" />
                <Row label="Partial records" value={pq.partial} tone="amber" />
                <Row label="Missing description" value={pq.no_description} tone="rose" />
                <Row label="Missing selling price" value={pq.no_selling_price} tone="amber" />
                <Row label="Action required" value={pq.action_required} />
              </div>
            ) : <div className="text-[10px] text-slate-400 pt-2">Loading…</div>}
          </div>

          <div className="border border-slate-200 rounded-lg bg-white p-3">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-900">
              <Database className="h-3.5 w-3.5 text-slate-500" /> Legacy import reconciliation
            </div>
            {recon ? (
              <div className="mt-2 space-y-1 text-[11px]">
                <Row label="Matched" value={recon.matched} tone="emerald" />
                <Row label="Possible match" value={recon.possible_match} tone="amber" />
                <Row label="Unmatched" value={recon.unmatched} />
                <Row label="Conflict" value={recon.conflict} tone="rose" />
                <Row label="Review required" value={recon.review_required} tone="amber" />
                <Row label="Duplicate customers" value={recon.duplicate_customers} />
                <Row label="Duplicate products" value={recon.duplicate_products} />
                <Row label="Missing product descriptions" value={recon.missing_product_descriptions} />
              </div>
            ) : <div className="text-[10px] text-slate-400 pt-2">Loading…</div>}
          </div>
        </div>
      </div>

      {(report?.action_items || []).length > 0 && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
          <div className="text-[11px] font-semibold text-amber-900 mb-1">
            {report.action_items.length} action item(s) before going live
          </div>
          <div className="flex flex-wrap gap-1.5">
            {report.action_items.map((a) => (
              <span key={a} className="text-[10px] px-2 py-0.5 rounded bg-white ring-1 ring-amber-200 text-amber-800">
                {a}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, tone }) {
  const toneCls = tone === "emerald" ? "text-emerald-700"
                                : tone === "amber"     ? "text-amber-700"
                                : tone === "rose"       ? "text-rose-700"
                                : "text-slate-900";
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-600">{label}</div>
      <div className={"font-mono font-semibold " + toneCls}>{value ?? "—"}</div>
    </div>
  );
}
